<template>
    <section class="supplier-evidences border my-3">
        <b-overlay :show="loading" rounded="sm">
            <div class="evidences-header">
                <div class="header-title">
                    <h6 class="mb-0">
                        <i class="material-icons header-icon">photo_library</i>
                        {{ $t('dashboard.evidences') || 'Evidencias de Servicios' }}
                    </h6>
                    <span v-if="startDate && endDate" class="date-range">
                        <i class="material-icons">date_range</i>
                        {{ formatDate(startDate) }} - {{ formatDate(endDate) }}
                    </span>
                </div>
            </div>

            <b-tabs v-if="evidences.length > 0" content-class="mt-3" fill>
                <!-- Tab Pendientes -->
                <b-tab active>
                    <template #title>
                        <i class="material-icons tab-icon">hourglass_empty</i>
                        {{ $t('dashboard.evidencesTabs.pending') }}
                        <b-badge variant="warning" class="ml-1">{{ pendingEvidences.length }}</b-badge>
                    </template>
                    <div v-if="pendingEvidences.length === 0" class="empty-state">
                        <i class="material-icons empty-icon">check_circle</i>
                        <p>{{ $t('dashboard.evidencesEmpty.pending') }}</p>
                    </div>
                    <div v-else class="table-responsive">
                        <b-table
                            :items="pendingEvidences"
                            :fields="fieldsPending"
                            small
                            hover
                            striped
                            bordered
                            :per-page="perPage"
                            :current-page="currentPagePending"
                            class="evidences-table"
                        >
                            <template #cell(service)="data">
                                <div class="service-cell">
                                    <strong>{{ data.item.service.serviceNumber }}</strong>
                                    <small class="text-muted d-block">{{ data.item.service.clientName }}</small>
                                </div>
                            </template>
                            <template #cell(route)="data">
                                <div class="route-cell">
                                    <div class="route-line">
                                        <i class="material-icons">trip_origin</i>
                                        <span>{{ truncateText(data.item.service.pickup, 30) }}</span>
                                    </div>
                                    <div class="route-line">
                                        <i class="material-icons">place</i>
                                        <span>{{ truncateText(data.item.service.dropoff, 30) }}</span>
                                    </div>
                                </div>
                            </template>
                            <template #cell(datetime)="data">
                                <span class="datetime-cell">{{ data.item.service.pickupDatetime }}</span>
                            </template>
                            <template #cell(details)="data">
                                <div class="details-cell">
                                    <span class="detail-badge">{{ getTripLabel(data.item.service.trip) }}</span>
                                    <span class="detail-text">{{ getPassengersText(data.item.service) }}</span>
                                </div>
                            </template>
                            <template #cell(tokenExpireAt)="data">
                                <div class="expire-date" :class="{ 'expired': isTokenExpired(data.item.tokenExpireAt) }">
                                    <div class="expire-time">
                                        <i class="material-icons">timer</i>
                                        {{ formatRemainingTime(data.item.tokenExpireAt) }}
                                    </div>
                                    <div><small class="expire-datetime">{{ data.item.tokenExpireAt }}</small></div>
                                </div>
                            </template>
                            <template #cell(actions)="data">
                                <div class="actions-cell">
                                    <b-button
                                        size="sm"
                                        variant="outline-secondary"
                                        @click="copyEvidenceLink(data.item.token)"
                                        :title="$t('dashboard.evidencesActions.copyLink')"
                                    >
                                        <i class="material-icons btn-icon">content_copy</i>
                                    </b-button>
                                    <b-button
                                        size="sm"
                                        variant="primary"
                                        @click="openEvidenceLink(data.item.token)"
                                        :title="$t('dashboard.evidencesActions.openForm')"
                                    >
                                        <i class="material-icons btn-icon">open_in_new</i>
                                    </b-button>
                                </div>
                            </template>
                        </b-table>
                        <b-pagination
                            v-if="pendingEvidences.length > perPage"
                            v-model="currentPagePending"
                            :total-rows="pendingEvidences.length"
                            :per-page="perPage"
                            size="sm"
                            align="center"
                            class="mt-2"
                        />
                    </div>
                </b-tab>

                <!-- Tab Aprobadas -->
                <b-tab>
                    <template #title>
                        <i class="material-icons tab-icon">check_circle</i>
                        {{ $t('dashboard.evidencesTabs.approved') }}
                        <b-badge variant="success" class="ml-1">{{ approvedEvidences.length }}</b-badge>
                    </template>
                    <div v-if="approvedEvidences.length === 0" class="empty-state">
                        <i class="material-icons empty-icon">inbox</i>
                        <p>{{ $t('dashboard.evidencesEmpty.approved') }}</p>
                    </div>
                    <div v-else class="table-responsive">
                        <b-table
                            :items="approvedEvidences"
                            :fields="fieldsApproved"
                            small
                            hover
                            striped
                            bordered
                            :per-page="perPage"
                            :current-page="currentPageApproved"
                            class="evidences-table"
                        >
                            <template #cell(service)="data">
                                <div class="service-cell">
                                    <strong>{{ data.item.service.serviceNumber }}</strong>
                                    <small class="text-muted d-block">{{ data.item.service.clientName }}</small>
                                </div>
                            </template>
                            <template #cell(route)="data">
                                <div class="route-cell">
                                    <div class="route-line">
                                        <i class="material-icons">trip_origin</i>
                                        <span>{{ truncateText(data.item.service.pickup, 30) }}</span>
                                    </div>
                                    <div class="route-line">
                                        <i class="material-icons">place</i>
                                        <span>{{ truncateText(data.item.service.dropoff, 30) }}</span>
                                    </div>
                                </div>
                            </template>
                            <template #cell(datetime)="data">
                                <span class="datetime-cell">{{ data.item.service.pickupDatetime }}</span>
                            </template>
                            <template #cell(details)="data">
                                <div class="details-cell">
                                    <span class="detail-badge">{{ getTripLabel(data.item.service.trip) }}</span>
                                    <span class="detail-text">{{ getPassengersText(data.item.service) }}</span>
                                </div>
                            </template>
                            <template #cell(providedAt)="data">
                                <span class="provided-date">
                                    <i class="material-icons">cloud_done</i>
                                    {{ data.item.evidenceProvidedAt || '-' }}
                                </span>
                            </template>
                        </b-table>
                        <b-pagination
                            v-if="approvedEvidences.length > perPage"
                            v-model="currentPageApproved"
                            :total-rows="approvedEvidences.length"
                            :per-page="perPage"
                            size="sm"
                            align="center"
                            class="mt-2"
                        />
                    </div>
                </b-tab>

                <!-- Tab Rechazadas -->
                <b-tab>
                    <template #title>
                        <i class="material-icons tab-icon">cancel</i>
                        {{ $t('dashboard.evidencesTabs.rejected') }}
                        <b-badge variant="danger" class="ml-1">{{ rejectedEvidences.length }}</b-badge>
                    </template>
                    <div v-if="rejectedEvidences.length === 0" class="empty-state">
                        <i class="material-icons empty-icon">thumb_up</i>
                        <p>{{ $t('dashboard.evidencesEmpty.rejected') }}</p>
                    </div>
                    <div v-else class="table-responsive">
                        <b-table
                            :items="rejectedEvidences"
                            :fields="fieldsRejected"
                            small
                            hover
                            striped
                            bordered
                            :per-page="perPage"
                            :current-page="currentPageRejected"
                            class="evidences-table rejected-table"
                        >
                            <template #cell(service)="data">
                                <div class="service-cell">
                                    <strong>{{ data.item.service.serviceNumber }}</strong>
                                    <small class="text-muted d-block">{{ data.item.service.clientName }}</small>
                                </div>
                            </template>
                            <template #cell(route)="data">
                                <div class="route-cell">
                                    <div class="route-line">
                                        <i class="material-icons">trip_origin</i>
                                        <span>{{ truncateText(data.item.service.pickup, 30) }}</span>
                                    </div>
                                    <div class="route-line">
                                        <i class="material-icons">place</i>
                                        <span>{{ truncateText(data.item.service.dropoff, 30) }}</span>
                                    </div>
                                </div>
                            </template>
                            <template #cell(datetime)="data">
                                <span class="datetime-cell">{{ data.item.service.pickupDatetime }}</span>
                            </template>
                            <template #cell(rejectionReason)="data">
                                <div class="rejection-cell">
                                    <i class="material-icons rejection-icon">error</i>
                                    <span>{{ getRejectionReason(data.item) || $t('dashboard.evidencesTable.noReasonSpecified') }}</span>
                                </div>
                            </template>
                        </b-table>
                        <b-pagination
                            v-if="rejectedEvidences.length > perPage"
                            v-model="currentPageRejected"
                            :total-rows="rejectedEvidences.length"
                            :per-page="perPage"
                            size="sm"
                            align="center"
                            class="mt-2"
                        />
                    </div>
                </b-tab>
            </b-tabs>

            <div v-else class="empty-state-global">
                <i class="material-icons empty-icon-large">photo_library</i>
                <p>{{ $t('dashboard.evidencesEmpty.global') }}</p>
            </div>
        </b-overlay>
    </section>
</template>

<script>
export default {
    name: 'SupplierEvidences',
    props: {
        filters: {
            type: Object,
            required: false
        },
        triggerSearch: {
            type: Number,
            required: false
        }
    },
    data() {
        return {
            loading: false,
            evidences: [],
            startDate: null,
            endDate: null,
            perPage: 10,
            currentPagePending: 1,
            currentPageApproved: 1,
            currentPageRejected: 1
        };
    },
    computed: {
        pendingEvidences() {
            return this.evidences.filter(e => {
                if (e.evidenceStatus !== 'pending') return false;
                if (!e.tokenExpireAt) return true;
                const expireDate = new Date(e.tokenExpireAt.replace(' ', 'T'));
                return expireDate > new Date();
            });
        },
        approvedEvidences() {
            return this.evidences.filter(e => e.evidenceStatus === 'approved');
        },
        rejectedEvidences() {
            return this.evidences.filter(e => e.evidenceStatus === 'rejected');
        },
        fieldsPending() {
            return [
                { key: 'service', label: this.$t('dashboard.evidencesTable.service'), thStyle: { width: '15%' } },
                { key: 'route', label: this.$t('dashboard.evidencesTable.route'), thStyle: { width: '25%' } },
                { key: 'datetime', label: this.$t('dashboard.evidencesTable.datetime'), thStyle: { width: '15%' } },
                { key: 'details', label: this.$t('dashboard.evidencesTable.details'), thStyle: { width: '13%' } },
                { key: 'tokenExpireAt', label: this.$t('dashboard.evidencesTable.expires'), thStyle: { width: '18%' } },
                { key: 'actions', label: this.$t('dashboard.evidencesTable.actions'), thStyle: { width: '14%' }, class: 'text-center' }
            ];
        },
        fieldsApproved() {
            return [
                { key: 'service', label: this.$t('dashboard.evidencesTable.service'), thStyle: { width: '18%' } },
                { key: 'route', label: this.$t('dashboard.evidencesTable.route'), thStyle: { width: '30%' } },
                { key: 'datetime', label: this.$t('dashboard.evidencesTable.datetime'), thStyle: { width: '17%' } },
                { key: 'details', label: this.$t('dashboard.evidencesTable.details'), thStyle: { width: '15%' } },
                { key: 'providedAt', label: this.$t('dashboard.evidencesTable.sentAt'), thStyle: { width: '20%' } }
            ];
        },
        fieldsRejected() {
            return [
                { key: 'service', label: this.$t('dashboard.evidencesTable.service'), thStyle: { width: '18%' } },
                { key: 'route', label: this.$t('dashboard.evidencesTable.route'), thStyle: { width: '27%' } },
                { key: 'datetime', label: this.$t('dashboard.evidencesTable.datetime'), thStyle: { width: '17%' } },
                { key: 'rejectionReason', label: this.$t('dashboard.evidencesTable.rejectionReason'), thStyle: { width: '38%' } }
            ];
        }
    },
    methods: {
        fetchData() {
            this.loading = true;
            this.$root.api.get(`/dashboard/evidences`, { params: { filters: this.filters } })
                .then((res) => {
                    this.evidences = res.data.evidences || [];
                    this.startDate = res.data.startDate || null;
                    this.endDate = res.data.endDate || null;
                })
                .catch(this.$root.handleRequestError)
                .finally(() => this.loading = false);
        },
        getTripLabel(trip) {
            const trips = {
                'A2H': 'A→H',
                'H2A': 'H→A',
                'H2H': 'H→H',
                'RT': 'RT'
            };
            return trips[trip] || trip;
        },
        getPassengersText(service) {
            const parts = [];
            if (service.adults) parts.push(`${service.adults}Ad`);
            if (service.children) parts.push(`${service.children}Ni`);
            if (service.infants) parts.push(`${service.infants}Inf`);
            return parts.join(' ') || '-';
        },
        truncateText(text, maxLength) {
            if (!text) return '';
            return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
        },
        openEvidenceLink(token) {
            const url = this.buildEvidenceUrl(token);
            window.open(url, '_blank');
        },
        copyEvidenceLink(token) {
            const url = this.buildEvidenceUrl(token);
            navigator.clipboard.writeText(url).then(() => {
                this.$root.showAlert(this.$t('dashboard.evidencesActions.linkCopied'), { variant: 'success' });
            }).catch(() => {
                this.$root.showAlert(this.$t('dashboard.evidencesActions.linkCopyError'), { variant: 'danger' });
            });
        },
        buildEvidenceUrl(token) {
            const baseUrl = process.env.VUE_APP_URL || '';
            return `${baseUrl}/supplier-evidences?token=${token}`;
        },
        isExpired(dateString) {
            if (!dateString) return false;
            const expireDate = new Date(dateString);
            return expireDate < new Date();
        },
        formatDate(dateString) {
            if (!dateString) return '';
            // Parsear la fecha manualmente para evitar problemas de timezone
            const [year, month, day] = dateString.split('-').map(Number);
            const date = new Date(year, month - 1, day);
            return date.toLocaleDateString('es-MX', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        },
        getRejectionReason(item) {
            const locale = this.$i18n?.locale || 'es';
            if (locale === 'es') {
                return item.evidenceRejectionReasonEs || item.evidenceRejectionReasonEn || '';
            }
            return item.evidenceRejectionReasonEn || item.evidenceRejectionReasonEs || '';
        },
        formatRemainingTime(tokenExpireAt) {
            if (!tokenExpireAt) {
                return this.$t('notifications.expiringSoon') || 'Expira pronto';
            }

            const expireDate = new Date(tokenExpireAt.replace(' ', 'T'));
            const now = new Date();
            const diffMs = expireDate - now;

            if (diffMs <= 0) {
                return this.$t('notifications.expired') || 'Expiró';
            }

            const diffMins = Math.floor(diffMs / 60000);
            const diffHours = Math.floor(diffMs / 3600000);

            const expiresIn = this.$t('notifications.expiresIn') || 'caduca en';

            if (diffHours >= 1) {
                const unitLabel = this.$t('notifications.hours') || 'hora(s)';
                return `${expiresIn} ${diffHours} ${unitLabel}`;
            } else {
                const unitLabel = this.$t('notifications.minutes') || 'minuto(s)';
                return `${expiresIn} ${diffMins} ${unitLabel}`;
            }
        },
        isTokenExpired(tokenExpireAt) {
            if (!tokenExpireAt) return false;
            const expireDate = new Date(tokenExpireAt.replace(' ', 'T'));
            return expireDate < new Date();
        }
    },
    mounted() {
        this.fetchData();
    },
    watch: {
        triggerSearch() {
            this.fetchData();
        }
    }
};
</script>

<style scoped>
.supplier-evidences {
    background: #fff;
    border-radius: 10px;
    padding: 20px;
    margin-bottom: 20px;
}

.evidences-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding-bottom: 15px;
    border-bottom: 2px solid #e2e8f0;
    flex-wrap: wrap;
    gap: 10px;
}

.evidences-header h5 {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #1e293b;
    font-weight: 600;
}

.header-title {
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
}

.date-range {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: #64748b;
    background: #f1f5f9;
    padding: 4px 12px;
    border-radius: 16px;
}

.date-range .material-icons {
    font-size: 16px;
    color: #3b82f6;
}

.header-icon {
    color: #3b82f6;
    font-size: 24px;
}

.evidences-summary {
    display: flex;
    gap: 8px;
}

.evidences-summary .badge {
    padding: 6px 12px;
    font-size: 12px;
    font-weight: 600;
}

.tab-icon {
    font-size: 18px;
    vertical-align: middle;
    margin-right: 4px;
}

/* Table Styles */
.evidences-table {
    font-size: 13px;
}

.evidences-table th {
    background: #f8fafc;
    border-bottom: 2px solid #e2e8f0;
    font-weight: 600;
    color: #475569;
    font-size: 12px;
    text-transform: uppercase;
}

.service-cell {
    line-height: 1.3;
}

.service-cell strong {
    color: #1e293b;
    font-size: 13px;
}

.service-cell small {
    font-size: 11px;
}

.route-cell {
    font-size: 12px;
}

.route-line {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-bottom: 2px;
}

.route-line .material-icons {
    font-size: 14px;
}

.datetime-cell {
    font-size: 12px;
    color: #475569;
}

.details-cell {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.detail-badge {
    background: #e0e7ff;
    color: #3730a3;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 600;
    display: inline-block;
    width: fit-content;
}

.detail-text {
    font-size: 11px;
    color: #64748b;
}

.btn-icon {
    font-size: 16px;
    vertical-align: middle;
}

.actions-cell {
    display: flex;
    gap: 4px;
    justify-content: center;
}

.expire-date {
    gap: 4px;
    font-size: 11px;
    color: #f59e0b;
}

.expire-date .material-icons {
    font-size: 14px;
}

.expire-date.expired {
    color: #ef4444;
    font-weight: 600;
}

.provided-date {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    color: #10b981;
}

.provided-date .material-icons {
    font-size: 14px;
}

.rejection-cell {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    background: #fef2f2;
    padding: 6px 8px;
    border-radius: 6px;
    border-left: 3px solid #ef4444;
}

.rejection-cell .rejection-icon {
    color: #ef4444;
    font-size: 16px;
    flex-shrink: 0;
}

.rejection-cell span {
    font-size: 12px;
    color: #991b1b;
    line-height: 1.3;
}

/* Empty States */
.empty-state {
    text-align: center;
    padding: 40px 20px;
    color: #94a3b8;
}

.empty-icon {
    font-size: 48px;
    margin-bottom: 12px;
    opacity: 0.5;
}

.empty-state p {
    margin: 0;
    font-size: 14px;
}

.empty-state-global {
    text-align: center;
    padding: 60px 20px;
    color: #94a3b8;
}

.empty-icon-large {
    font-size: 64px;
    margin-bottom: 16px;
    opacity: 0.4;
}

.empty-state-global p {
    margin: 0;
    font-size: 16px;
}

/* Responsive */
@media (max-width: 768px) {
    .evidences-header {
        flex-direction: column;
        align-items: flex-start;
    }

    .route-cell {
        font-size: 11px;
    }

    .route-line .material-icons {
        font-size: 12px;
    }
}
</style>
